/**
 * Which app's MCP server a key opens. Each app has its own server at `/mcp/<app>` and its own
 * tools; a key for one never opens another.
 *
 * - `athlete` keys belong to one person and act on their own data.
 * - `coach` keys belong to a business and act inside it, with the permissions of the member who
 *   created them.
 * - `admin` keys cover the whole platform, with the admin role of the person who created them.
 */
export enum McpApp {
  ATHLETE = "athlete",
  COACH = "coach",
  ADMIN = "admin",
}

/** How far a key reaches into one tool group: reading only, or reading and changing. */
export enum McpAccess {
  READ = "read",
  WRITE = "write",
}

/**
 * The tool groups of each app's MCP server. A key holds a scope per group (`calendar:read`,
 * `calendar:write`), and a tool is listed and callable only when the key holds its scope.
 */
export const MCP_TOOL_GROUPS = {
  [McpApp.ATHLETE]: ["calendar", "discover", "training", "nutrition", "progress", "profile"],
  [McpApp.COACH]: ["schedule", "clients", "team", "business", "payments"],
  [McpApp.ADMIN]: ["exercises", "health", "accounts", "blog", "help"],
} as const satisfies Record<McpApp, readonly string[]>;

export type McpToolGroup<App extends McpApp = McpApp> = (typeof MCP_TOOL_GROUPS)[App][number];

/** One scope a key holds: a tool group and how far into it. `write` includes `read`. */
export type McpScope = `${string}:${McpAccess}`;

export const toMcpScope = (group: string, access: McpAccess): McpScope => `${group}:${access}`;

/** Every scope an app's keys can hold, write before read so a picker can show the stronger one first. */
export const mcpScopesFor = (app: McpApp): McpScope[] =>
  MCP_TOOL_GROUPS[app].flatMap((group) => [toMcpScope(group, McpAccess.WRITE), toMcpScope(group, McpAccess.READ)]);

/** Whether a key's scopes let it use a tool of `group` that needs `access`. */
export const mcpScopesAllow = (scopes: readonly string[], group: string, access: McpAccess): boolean =>
  scopes.includes(toMcpScope(group, McpAccess.WRITE)) ||
  (access === McpAccess.READ && scopes.includes(toMcpScope(group, McpAccess.READ)));
